import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_API_URL}/packages`;

export interface ApplicationPackage {
  id?: number;
  name?: string;
  description?: string;
  active?: boolean;
  price?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApplicationPackageRequest {
  name: string;
  description?: string;
  active?: boolean;
  price?: number;
}

const getAllPackages = async (): Promise<ApplicationPackage[]> => {
  const resp = await axios.get(API_URL);
  return resp.data;
};

const createPackage = async (data: ApplicationPackageRequest): Promise<ApplicationPackage> => {
  const resp = await axios.post(API_URL, data);
  return resp.data;
};

const toPackageRequestBody = (data: ApplicationPackageRequest): ApplicationPackageRequest => ({
  name: data.name,
  description: data.description,
  active: data.active,
  price: data.price,
});

const updatePackage = async (args: {
  id: number;
  data: ApplicationPackageRequest;
}): Promise<ApplicationPackage> => {
  const resp = await axios.put(`${API_URL}/${args.id}`, toPackageRequestBody(args.data));
  return resp.data;
};

const deletePackage = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

export function useApplicationPackages() {
  const queryClient = useQueryClient();

  const packagesQuery = useQuery<ApplicationPackage[], Error>({
    queryKey: ["application-packages"],
    queryFn: getAllPackages,
  });

  const createPackageMutation = useMutation({
    mutationFn: createPackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application-packages"] });
    },
  });

  const updatePackageMutation = useMutation({
    mutationFn: updatePackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application-packages"] });
    },
  });

  const deletePackageMutation = useMutation({
    mutationFn: deletePackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application-packages"] });
    },
  });

  return {
    packagesQuery,
    createPackageMutation,
    updatePackageMutation,
    deletePackageMutation,
  };
}
